import React, { useState, useEffect } from 'react';
import { financeApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiPlus, HiPencil, HiX, HiCurrencyRupee, HiCalendar, HiTag, HiTrash, HiCog } from 'react-icons/hi';
import ConfirmDialog from '../../components/ConfirmDialog';
import { formatValidationErrors } from '../../utils/errorHandler';
import { todayYmdIST, formatCalendarDateDMY } from '../../utils/timezoneDisplay';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'card', label: 'Card' },
  { value: 'cheque', label: 'Cheque' },
];

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null });
  const [showForm, setShowForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [formData, setFormData] = useState({
    category_id: '',
    description: '',
    amount: '',
    expense_date: todayYmdIST(),
    payment_mode: 'cash',
    vendor: '',
    invoice_number: '',
    notes: '',
    status: 'pending'
  });
  const [filters, setFilters] = useState({
    category_id: '',
    payment_mode: '',
    date_from: '',
    date_to: '',
    page: 1
  });

  useEffect(() => {
    fetchCategories();
    fetchExpenses();
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const response = await financeApi.getExpenseCategories();
      setCategories(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch expense categories');
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: filters.page,
        limit: 20
      };
      const response = await financeApi.getExpenses(params);
      setExpenses(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingExpense(null);
    setFormData({
      category_id: '',
      description: '',
      amount: '',
      expense_date: todayYmdIST(),
      payment_mode: 'cash',
      vendor: '',
      invoice_number: '',
      notes: '',
      status: 'pending'
    });
    setShowForm(true);
  };

  const handleEdit = (expense) => {
    // Helper function to format date for input field (avoids timezone issues)
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      
      // If already in YYYY-MM-DD format, return as is (no timezone conversion)
      if (typeof dateString === 'string') {
        // Check for YYYY-MM-DD format (with or without time)
        const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          // Return the date part directly without timezone conversion
          return dateMatch[0];
        }
      }
      
      // Fallback: try to parse as Date but use local date components to avoid timezone shift
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          // Use local date components instead of UTC to avoid timezone issues
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      } catch (e) {
        console.error('Error parsing date:', e);
      }
      return '';
    };

    setEditingExpense(expense);
    setFormData({
      category_id: expense.category_id,
      description: expense.description || '',
      amount: expense.amount,
      expense_date: formatDateForInput(expense.expense_date) || todayYmdIST(),
      payment_mode: expense.payment_mode || 'cash',
      vendor: expense.vendor || '',
      invoice_number: expense.invoice_number || '',
      notes: expense.notes || '',
      status: expense.status || 'pending'
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingExpense) {
        await financeApi.updateExpense(editingExpense.id, formData);
        toast.success('Expense updated successfully');
      } else {
        await financeApi.createExpense(formData);
        toast.success('Expense created successfully');
      }
      setShowForm(false);
      fetchExpenses();
    } catch (error) {
      toast.error(formatValidationErrors(error));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
      reimbursed: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Reimbursed' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`badge ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPaymentModeLabel = (mode) => {
    return mode ? mode.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-';
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      description: '',
      is_active: true
    });
    setShowCategoryForm(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      description: category.description || '',
      is_active: category.is_active === 1 || category.is_active === true
    });
    setShowCategoryForm(true);
  };

  const handleSubmitCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await financeApi.updateExpenseCategory(editingCategory.id, categoryFormData);
        toast.success('Category updated successfully');
      } else {
        await financeApi.createExpenseCategory(categoryFormData);
        toast.success('Category created successfully');
      }
      setShowCategoryForm(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save category');
    }
  };

  const handleDeleteCategory = (id) => {
    setConfirmDialog({
      isOpen: true,
      onConfirm: async () => {
        try {
          await financeApi.deleteExpenseCategory(id);
          toast.success('Category deleted successfully');
          fetchCategories();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete category');
        }
      }
    });
  };

  const handleToggleCategoryActive = async (category) => {
    try {
      await financeApi.updateExpenseCategory(category.id, {
        is_active: !(category.is_active === 1 || category.is_active === true)
      });
      toast.success('Category updated successfully');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update category');
    }
  };

  if (loading && expenses.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  const totalAmount = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Expense Management</h2>
          <p className="text-sm text-gray-500">Track and manage all expenses</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleAddCategory}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
          >
            <HiCog className="w-5 h-5" />
            Manage Categories
          </button>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <HiPlus className="w-5 h-5" />
            Add Expense
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filters.category_id}
              onChange={(e) => setFilters({ ...filters, category_id: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
            <select
              value={filters.payment_mode}
              onChange={(e) => setFilters({ ...filters, payment_mode: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All modes</option>
              {PAYMENT_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() =>
                setFilters({
                  category_id: '',
                  payment_mode: '',
                  date_from: '',
                  date_to: '',
                  page: 1,
                })
              }
              className="w-full px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="card bg-gradient-to-r from-red-50 to-red-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HiCurrencyRupee className="w-8 h-8 text-red-600" />
            <div>
              <p className="text-sm text-gray-600">Total Expenses</p>
              <p className="text-2xl font-bold text-red-700">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Records</p>
            <p className="text-2xl font-bold text-gray-900">{expenses.length}</p>
          </div>
        </div>
      </div>

      {/* Expenses List */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left p-3 font-semibold text-gray-700">Date</th>
                <th className="text-left p-3 font-semibold text-gray-700">Category</th>
                <th className="text-left p-3 font-semibold text-gray-700">Description</th>
                <th className="text-right p-3 font-semibold text-gray-700">Amount</th>
                <th className="text-left p-3 font-semibold text-gray-700">Payment Mode</th>
                <th className="text-left p-3 font-semibold text-gray-700">Vendor</th>
                <th className="text-center p-3 font-semibold text-gray-700">Status</th>
                <th className="text-center p-3 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center p-8 text-gray-500">
                    No expenses found
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-600">
                      {formatCalendarDateDMY(expense.expense_date)}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <HiTag className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{expense.category_name}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-gray-900">{expense.description}</div>
                      {expense.invoice_number && (
                        <div className="text-xs text-gray-500">Invoice: {expense.invoice_number}</div>
                      )}
                      {expense.notes && (
                        <div className="text-xs text-gray-500 mt-1">{expense.notes}</div>
                      )}
                    </td>
                    <td className="p-3 text-right font-semibold text-red-600">
                      ₹{parseFloat(expense.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-3 text-sm text-gray-600">
                      {getPaymentModeLabel(expense.payment_mode)}
                    </td>
                    <td className="p-3 text-sm text-gray-600">
                      {expense.vendor || '-'}
                    </td>
                    <td className="p-3 text-center">
                      {getStatusBadge(expense.status)}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(expense)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                          title="Edit Expense"
                        >
                          <HiPencil className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expense Form Modal */}
      {showForm && (
        <div className="modal-shell flex items-center justify-center">
          <div className="modal-panel max-w-2xl">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editingExpense ? 'Edit Expense' : 'Add New Expense'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expense Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.expense_date}
                    onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter expense description"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Mode <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.payment_mode}
                    onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    {PAYMENT_MODES.map((mode) => (
                      <option key={mode.value} value={mode.value}>
                        {mode.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                  <input
                    type="text"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Vendor name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                  <input
                    type="text"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Invoice/Bill number"
                  />
                </div>
              </div>

              {editingExpense && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="reimbursed">Reimbursed</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Additional notes (optional)"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingExpense ? 'Update Expense' : 'Create Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryForm && (
        <div className="modal-shell flex items-center justify-center">
          <div className="modal-panel max-w-4xl">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Manage Expense Categories</h3>
              <button
                onClick={() => setShowCategoryForm(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {/* Add/Edit Category Form */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">
                  {editingCategory ? 'Edit Category' : 'Add New Category'}
                </h4>
                <form onSubmit={handleSubmitCategory} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={categoryFormData.name}
                        onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., Office Supplies"
                        required
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={categoryFormData.is_active}
                          onChange={(e) => setCategoryFormData({ ...categoryFormData, is_active: e.target.checked })}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Active</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={categoryFormData.description}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Category description (optional)"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCategoryForm(false);
                        setEditingCategory(null);
                      }}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      {editingCategory ? 'Update Category' : 'Create Category'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Categories List */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Existing Categories</h4>
                {categories.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No categories found. Create one above.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left p-3 font-semibold text-gray-700">Name</th>
                          <th className="text-left p-3 font-semibold text-gray-700">Description</th>
                          <th className="text-center p-3 font-semibold text-gray-700">Status</th>
                          <th className="text-right p-3 font-semibold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((category) => (
                          <tr key={category.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="p-3 font-medium text-gray-900">{category.name}</td>
                            <td className="p-3 text-gray-600">{category.description || '-'}</td>
                            <td className="p-3 text-center">
                              <button
                                onClick={() => handleToggleCategoryActive(category)}
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  (category.is_active === 1 || category.is_active === true)
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {(category.is_active === 1 || category.is_active === true) ? 'Active' : 'Inactive'}
                              </button>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleEditCategory(category)}
                                  className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                                  title="Edit"
                                >
                                  <HiPencil className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteCategory(category.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                  title="Delete"
                                >
                                  <HiTrash className="w-5 h-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, onConfirm: null })}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        title="Delete Category"
        message="Are you sure you want to delete this category? This action cannot be undone if there are expenses using this category."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Expenses;

